const ALL_WHITESPACE = /\s+/g
const PLATE_NOISE = /[\s-]+/g
const NON_DIGITS = /\D+/g
const MC_PREFIX = /^MC[\s-]*/i

/** A VIN is always this long — ISO 3779. */
export const FULL_VIN_LENGTH = 17

/**
 * VINs never carry I, O or Q (ISO 3779 reserves them out to avoid confusion with
 * 1 and 0). O and I are unambiguous typing mistakes we can repair; Q has no digit
 * twin, so it has to be rejected. Mirrors sanitization.py.
 */
const VIN_CONFUSABLES = /[OI]/g
const VIN_CONFUSABLE_DIGITS: Record<string, string> = { O: '0', I: '1' }

/** ISO 3779's character values for the check digit. I, O and Q have none — no VIN carries them. */
const VIN_TRANSLITERATION: Record<string, number> = Object.fromEntries([
  ...[...'0123456789'].map((char) => [char, Number(char)] as const),
  ...[...'ABCDEFGH'].map((char, index) => [char, index + 1] as const),
  ...[...'JKLMN'].map((char, index) => [char, index + 1] as const),
  ...[...'STUVWXYZ'].map((char, index) => [char, index + 2] as const),
  ['P', 7] as const,
  ['R', 9] as const,
])
const VIN_CHECK_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2]
const VIN_CHECK_POSITION = 8

export function sanitizeVin(raw: string | null | undefined): string {
  if (!raw) return ''
  const vin = raw.replace(ALL_WHITESPACE, '').toUpperCase()
  // Full length only — a shorter string may be a partial id a guess would corrupt.
  if (vin.length !== FULL_VIN_LENGTH) return vin
  return vin.replace(VIN_CONFUSABLES, (char) => VIN_CONFUSABLE_DIGITS[char])
}

/**
 * The reason a VIN can't be right, or null. Mirrors sanitization.py's
 * `vin_carrier_error` — the checks a carrier rejects a submission over, so the
 * message has to be plain enough for the customer who typed it.
 */
export function vinError(raw: string | null | undefined): string | null {
  const vin = sanitizeVin(raw)
  // Blank is "not filled in yet", which the required-field rules already cover.
  if (!vin) return null
  if (vin.includes('Q')) return 'A VIN never contains the letter Q. Check this one against the vehicle.'
  if (vin.length !== FULL_VIN_LENGTH) {
    return `A VIN is 17 characters — this one is ${vin.length}. Check it against the vehicle.`
  }
  const illegal = [...new Set([...vin].filter((char) => !(char in VIN_TRANSLITERATION)))]
  if (illegal.length) {
    return `A VIN never contains ${illegal.join(' or ')}. Check this one against the vehicle.`
  }
  return null
}

/**
 * A VIN that is the right shape but fails its ISO 3779 check digit. A strong hint
 * it was misread, not proof — some small trailer builders stamp VINs that fail it —
 * so this warns and never blocks.
 */
export function vinCheckDigitWarning(raw: string | null | undefined): string | null {
  const vin = sanitizeVin(raw)
  if (!vin || vinError(vin)) return null
  const total = [...vin].reduce(
    (sum, char, index) => sum + VIN_TRANSLITERATION[char] * VIN_CHECK_WEIGHTS[index],
    0
  )
  const remainder = total % 11
  const expected = remainder === 10 ? 'X' : String(remainder)
  if (vin[VIN_CHECK_POSITION] === expected) return null
  return `This VIN looks misread — character 9 should be ${expected}. Double-check it before you continue.`
}

export function sanitizeLicensePlate(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.replace(PLATE_NOISE, '').toUpperCase()
}

export function sanitizeLicenseState(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.trim().toUpperCase()
}

/** Values typed to fill the field rather than to name a license. Mirrors driver_license.py. */
export const PLACEHOLDER_LICENSE_NUMBERS = new Set([
  'X00000000',
  'DL123456',
  'UNKNOWN',
  'N/A',
  'NA',
  'NONE',
  'TBD',
  'TEST',
  'SAME',
])

/** The separators a license is printed with; the server stores the number without them. */
const LICENSE_SEPARATORS = /[\s.-]+/g

/** The number the server will store, bar North Carolina's zero padding; blank for a placeholder. */
export function sanitizeDriverLicenseNumber(raw: string | null | undefined): string {
  if (!raw) return ''
  const cleaned = raw.toUpperCase().replace(/[<>]/g, '').replace(LICENSE_SEPARATORS, '')
  return PLACEHOLDER_LICENSE_NUMBERS.has(cleaned) || /^0+$/.test(cleaned) ? '' : cleaned
}

const NC_LICENSE_DIGITS = 12

/** The number exactly as the server stores it for this state, NC zero-padded included. Mirrors license_key. */
export function storedLicenseNumber(
  raw: string | null | undefined,
  state: string | null | undefined
): string {
  const cleaned = sanitizeDriverLicenseNumber(raw)
  if (sanitizeLicenseState(state) !== 'NC' || !/^\d+$/.test(cleaned)) return cleaned
  const significant = cleaned.replace(/^0+/, '')
  return significant.length <= NC_LICENSE_DIGITS ? significant.padStart(NC_LICENSE_DIGITS, '0') : cleaned
}

/** Blank as the server reads it: a value of only spaces counts as none. */
export function isBlank(value: string | number | null | undefined): boolean {
  return typeof value === 'string' ? !value.trim() : !value
}

export function sanitizeEmail(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.trim().toLowerCase()
}

export function sanitizeName(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.trim().replace(ALL_WHITESPACE, ' ')
}

export function sanitizeDotNumber(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.replace(NON_DIGITS, '')
}

export function sanitizeMcNumber(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.trim().replace(MC_PREFIX, '').replace(NON_DIGITS, '')
}
